/**
 * ManageTrainingsTab
 *
 * Company-side workflow: assign trainings by role/group, create new trainings,
 * upload certificates/materials. Placeholder UI; backend wiring TBD.
 * PHI-safe: role/group selection only; no patient data.
 */

import {
  Button,
  Card,
  FileInput,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconUpload, IconUserPlus, IconFilePlus } from '@tabler/icons-react';
import { useState } from 'react';

/** Assign to role/group (in production: from API / RBAC) */
const MOCK_ASSIGN_TO_GROUPS = [
  { value: 'all-providers', label: 'All providers' },
  { value: 'supervisors', label: 'Supervisors' },
  { value: 'clinicians', label: 'Clinicians' },
  { value: 'new-hires', label: 'New hires' },
  { value: 'iop-group', label: 'IOP / Group therapy providers' },
  { value: 'admin', label: 'Admins' },
];

/** Placeholder training catalog (in production: from API) */
const MOCK_TRAINING_CATALOG = [
  { value: 't1', label: 'HIPAA Privacy & Security' },
  { value: 't2', label: 'Fraud, Waste & Abuse (FWA)' },
  { value: 't3', label: 'IOP/Group Therapy Mandatory Training' },
  { value: 't4', label: 'Suicide Risk Assessment Annual Update' },
];

export function ManageTrainingsTab(): JSX.Element {
  const [assignLoading, setAssignLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const assignForm = useForm({
    initialValues: { assignToRoleId: '', trainingId: '', dueDate: '' },
    validate: {
      assignToRoleId: (v) => (v ? null : 'Select a role or group'),
      trainingId: (v) => (v ? null : 'Select a training'),
    },
  });

  const createForm = useForm({
    initialValues: {
      name: '',
      required: 'optional' as 'required' | 'optional',
      description: '',
      dueDate: '',
    },
    validate: { name: (v) => (v?.trim() ? null : 'Name is required') },
  });

  const handleAssign = assignForm.onSubmit(async (values) => {
    setAssignLoading(true);
    try {
      // Placeholder: call API to assign training to role/group
      await new Promise((r) => setTimeout(r, 500));
      const groupLabel = MOCK_ASSIGN_TO_GROUPS.find((g) => g.value === values.assignToRoleId)?.label ?? 'role';
      showNotification({ color: 'green', message: `Training assigned to ${groupLabel}` });
      assignForm.reset();
    } finally {
      setAssignLoading(false);
    }
  });

  const handleCreate = createForm.onSubmit(async () => {
    setCreateLoading(true);
    try {
      // Placeholder: call API to create training
      await new Promise((r) => setTimeout(r, 500));
      showNotification({ color: 'green', message: 'Training created successfully' });
      createForm.reset();
    } finally {
      setCreateLoading(false);
    }
  });

  const handleUpload = (files: File | null): void => {
    if (!files) return;
    setUploadLoading(true);
    // Placeholder: upload certificate/material
    setTimeout(() => {
      showNotification({ color: 'green', message: `Uploaded: ${files.name}` });
      setUploadLoading(false);
    }, 500);
  };

  return (
    <Stack gap="lg">
      {/* Assign trainings */}
      <Card withBorder>
        <Group gap="xs" mb="md">
          <IconUserPlus size={20} stroke={1.5} />
          <Title order={4}>Assign trainings</Title>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Assign a training from the catalog to a role or group. Everyone in that role will see it in their Current &
          past trainings tab.
        </Text>
        <form onSubmit={handleAssign}>
          <Stack gap="sm">
            <Select
              label="Assign to"
              placeholder="Select role or group"
              data={MOCK_ASSIGN_TO_GROUPS}
              size="sm"
              {...assignForm.getInputProps('assignToRoleId')}
            />
            <Select
              label="Training"
              placeholder="Select training"
              data={MOCK_TRAINING_CATALOG}
              size="sm"
              {...assignForm.getInputProps('trainingId')}
            />
            <TextInput
              label="Due date (optional)"
              type="date"
              size="sm"
              {...assignForm.getInputProps('dueDate')}
            />
            <Button type="submit" size="sm" color="unifyGold" loading={assignLoading}>
              Assign training
            </Button>
          </Stack>
        </form>
      </Card>

      {/* Create training */}
      <Card withBorder>
        <Group gap="xs" mb="md">
          <IconFilePlus size={20} stroke={1.5} />
          <Title order={4}>Create training</Title>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Add a new training to the catalog. You can then assign it to roles or groups.
        </Text>
        <form onSubmit={handleCreate}>
          <Stack gap="sm">
            <TextInput
              label="Training name"
              placeholder="e.g. Cultural Competency (CE)"
              size="sm"
              required
              {...createForm.getInputProps('name')}
            />
            <Select
              label="Type"
              data={[
                { value: 'required', label: 'Required' },
                { value: 'optional', label: 'Optional' },
              ]}
              size="sm"
              {...createForm.getInputProps('required')}
            />
            <TextInput
              label="Description (optional)"
              placeholder="Brief description"
              size="sm"
              {...createForm.getInputProps('description')}
            />
            <TextInput
              label="Default due date (optional)"
              type="date"
              size="sm"
              {...createForm.getInputProps('dueDate')}
            />
            <Button type="submit" size="sm" color="unifyGold" loading={createLoading}>
              Create training
            </Button>
          </Stack>
        </form>
      </Card>

      {/* Upload certificates / materials */}
      <Card withBorder>
        <Group gap="xs" mb="md">
          <IconUpload size={20} stroke={1.5} />
          <Title order={4}>Upload certificates or materials</Title>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Upload a certificate or training material (PDF, image). In production this will be linked to a provider and
          training.
        </Text>
        <FileInput
          placeholder="Choose file"
          accept="application/pdf,image/*"
          size="sm"
          onChange={handleUpload}
          disabled={uploadLoading}
        />
      </Card>
    </Stack>
  );
}
